import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { PackagingTypeEnum } from '../../../enum/packaging-type.enum';
import { WeightUnitEnum } from '../../../enum/weight-unit.enum';
import { PackageFormComponent } from '../package-form/package-form.component';
import { MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { packageEditorSaveRequest, packageEditorSetPackage } from '../../../store/package-editor/package-editor.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { AsyncPipe } from '@angular/common';
import { IconComponent } from '../../shared/icon/icon.component';
import {
    selectPackageEditorLoading,
    selectPackageEditorPackage,
    selectPackageEditorSaving
} from '../../../store/package-editor/package-editor.state';
import { combineLatest, of, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { createCommodityFormGroup } from '../../../constants/commodity-form-group';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { emptyPackage } from '../../../constants/empty-package';
import { PackagingTypeName } from '../../../utils/packaging-type-name';
import { CurrencyEnum } from '../../../enum/currency.enum';
import { DimensionUnitEnum } from '../../../enum/dimension-unit.enum';

@Component({
    selector: 'app-package-edit',
    imports: [
    PackageFormComponent,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    IconComponent,
    MatProgressSpinner,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem
],
    templateUrl: './package-edit.component.html',
    styleUrl: './package-edit.component.scss'
})
export class PackageEditComponent implements OnInit, OnDestroy {

    public formGroup = new FormGroup({
        description: new FormControl('', Validators.required),
        weight: new FormControl(1, Validators.min(0.01)),
        length: new FormControl(1, Validators.min(0.01)),
        width: new FormControl(1, Validators.min(0.01)),
        height: new FormControl(1, Validators.min(0.01)),
        value: new FormControl(0),
        currency: new FormControl(CurrencyEnum.USD),
        packagingType: new FormControl(PackagingTypeEnum.CUSTOMER_PACKAGING),
        weightUnit: new FormControl(WeightUnitEnum.KG),
        commodities: new FormArray([]),
    });

    public KG = WeightUnitEnum.KG;
    public LB = WeightUnitEnum.LB;

    private stateSubscription: Subscription;

    constructor(private dialogRef: MatDialogRef<PackageEditComponent>, private store: Store<AppState>) {}

    public saving$ = this.store.select(selectPackageEditorSaving);
    public loading$ = this.store.select(selectPackageEditorLoading);
    public disableSave$ = combineLatest([ this.saving$, this.loading$, this.formGroup.valueChanges ]).pipe(
        startWith([ false, false, this.formGroup.value ]),
        map(([ saving, loading, value ]) => saving
            || loading
            || this.formGroup.invalid
        )
    );
    public packagingType$ = this.formGroup.valueChanges.pipe(
        startWith(emptyPackage),
        map((pkg) => pkg.packagingType),
    );
    public packagingTypeDisplay$ = this.packagingType$.pipe(
        map((pkg) => PackagingTypeName.describe(pkg)),
    );
    public packagingTypes$ = of(Object.values(PackagingTypeEnum)).pipe(
        map((types) => types.map((t) => ({ code: t, name: PackagingTypeName.describe(t) })))
    );
    public weightUnit$ = this.formGroup.valueChanges.pipe(
        startWith(emptyPackage),
        map((pkg) => pkg.weightUnit)
    );
    public weightUnitDisplay$ = this.weightUnit$.pipe(
        map((weightUnit) => weightUnit === WeightUnitEnum.LB ? $localize`Pounds/Inches` : $localize`KG/CM`)
    );
    public currency$ = this.formGroup.valueChanges.pipe(
        startWith(emptyPackage),
        map((pkg) => pkg.currency)
    );
    public currencies$ = of(Object.values(CurrencyEnum));
    public dimensionUnit$ = this.weightUnit$.pipe(
        map((u) => u === WeightUnitEnum.LB ? DimensionUnitEnum.IN : DimensionUnitEnum.CM)
    );

    public formCommodities(): FormArray {
        return this.formGroup.get('commodities') as FormArray;
    }

    ngOnInit(): void {
        this.stateSubscription = this.store.select(selectPackageEditorPackage).subscribe((pkg) => {
            const formCommodities = this.formGroup.get('commodities') as FormArray;
            while (formCommodities.length < pkg.commodities.length) {
                formCommodities.push(createCommodityFormGroup(), { emitEvent: false });
            }
            while (formCommodities.length > pkg.commodities.length) {
                formCommodities.removeAt(formCommodities.length - 1, { emitEvent: false });
            }
            this.formGroup.patchValue(pkg);
        });
    }

    ngOnDestroy(): void {
        this.stateSubscription.unsubscribe();
    }

    addCommodity(): void {
        this.formCommodities().push(createCommodityFormGroup());
    }

    deleteCommodity(index?: number): void {
        if (index === undefined) {
            index = this.formCommodities().controls.length - 1;
        }
        this.formCommodities().removeAt(index);
    }

    close(): void {
        this.dialogRef.close(null);
    }

    setCurrency(currency: CurrencyEnum): void {
        this.formGroup.get('currency').setValue(currency);
    }

    setWeightUnit(weightUnit: WeightUnitEnum): void {
        this.formGroup.get('weightUnit').setValue(weightUnit);
    }

    setPackagingType(packagingType: PackagingTypeEnum): void {
        this.formGroup.get('packagingType').setValue(packagingType);
    }

    submit(): void {
        this.store.dispatch(packageEditorSetPackage({ pkg: this.formGroup.value }));
        this.store.dispatch(packageEditorSaveRequest());
    }

}
